import {createPortal} from 'react-dom';
import {InputProperties} from '@priority-software/priority-style-react/dist/components/Inputs/Input/Input';
import React, {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState
} from 'react';
import cx from 'classnames';
import {
    DropdownItem,
    Icon,
    Input,
    PriorityIcon,
    Scrollbar,
    Text,
    useKeyboardNavigation
} from '@priority-software/priority-style-react';
import {AutomationChoose} from '../../features/Automations/reducer/model';
import {isRTL} from '../../Localization';
import {texts} from '../../features/Automations/texts';
import styles from './styles.module.scss';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';

type Props = {
    componentClasses?: string;
    onItemClick: (value: DropdownItemData) => void;
    data: Array<DropdownItemData>;
    autoHeightMax?: number;
    autoHeightMin?: number;
    selectedValue?: number | string | boolean;
    showSearch?: boolean;
    onFocusChange?: (item: DropdownItemData, index: number) => void;
    placeholder?: string;
    noOptionsText?: React.ReactNode;
    itemRenderer?: (item: DropdownItemData) => React.ReactNode;
    inputPortalId?: string;
    inputProps?: InputProperties;
    onEnter?: () => void;
    onDoubleClick?: () => void;
};

export const SearchableList = forwardRef((props: Props, ref: ForwardedRef<HTMLDivElement>) => {
    const {
        componentClasses,
        data,
        onItemClick,
        autoHeightMax,
        autoHeightMin = 200,
        selectedValue,
        onFocusChange,
        placeholder,
        noOptionsText,
        itemRenderer,
        inputPortalId,
        showSearch = true,
        onEnter,
        onDoubleClick,
        inputProps = {}
    } = props;
    const [value, setValue] = useState('');
    const [isMounted, setIsMounted] = useState(false);

    const renderData = useMemo(() => {
        return value === ''
            ? data
            : data.filter(item => {
                  const searchValue = value.toLocaleLowerCase();
                  return (
                      item.label.toLocaleLowerCase().includes(searchValue) ||
                      item.description?.toLocaleLowerCase().includes(searchValue)
                  );
              });
    }, [value, data]);

    const handleEnter = useCallback(
        (e: KeyboardEvent, focusedIndex: number) => {
            if (e.key === 'Enter') {
                const isButtonElement = e.target instanceof HTMLButtonElement;
                // Handle selected option only if event is not triggered by button
                if (!isButtonElement) {
                    e.preventDefault();

                    const focusedOption = renderData[focusedIndex];

                    if (focusedOption) {
                        onItemClick(focusedOption);
                        onEnter?.();
                    }
                }
            }
        },
        [onItemClick, renderData, onEnter]
    );

    const {focusedIndex, handleNavigationKeys, setFocusedIndex} = useKeyboardNavigation({
        optionsCount: renderData.length,
        defaultFocusedIndex: 0,
        handleEnter
    });

    const onSelect = useCallback(
        (item: AutomationChoose, index: number) => {
            onItemClick(item);
            setFocusedIndex(index);
        },
        [setFocusedIndex, onItemClick]
    );

    const handleValue = useCallback(
        e => {
            setValue(e.target.value);
            setFocusedIndex(0);
        },
        [setFocusedIndex]
    );

    useLayoutEffect(() => {
        setIsMounted(true);
    }, [isMounted]);

    useEffect(() => {
        if (onFocusChange) {
            onFocusChange(renderData[focusedIndex], focusedIndex);
        }
    }, [focusedIndex, renderData]);

    useEffect(() => {
        window.addEventListener('keydown', handleNavigationKeys, true);
        return () => window.removeEventListener('keydown', handleNavigationKeys, true);
    }, [handleNavigationKeys]);

    const {children: inputChildren, ...restInputProps} = inputProps;

    const searchInput = (
        <Input
            componentClasses={styles.searchInput}
            roundedCorners
            value={value}
            onChange={handleValue}
            placeholder={placeholder || texts.search_placeholder}
            onKeyDown={e => handleEnter(e, focusedIndex)}
            {...restInputProps}
        >
            {!!value ? (
                <Icon
                    icon={PriorityIcon.closeBig}
                    size="small"
                    onClick={() => setValue('')}
                    style={{marginInlineEnd: 4}}
                />
            ) : null}
            {inputChildren}
        </Input>
    );

    const container = useMemo(() => {
        return document.getElementById(inputPortalId);
    }, [isMounted]);

    return (
        <div className={cx(styles.component, componentClasses)} ref={ref}>
            {showSearch ? (container ? createPortal(searchInput, container) : searchInput) : null}

            <Scrollbar
                autoHeight
                autoHeightMin={autoHeightMin || 200}
                autoHeightMax={autoHeightMax}
                rtl={isRTL()}
                shiftVerticalScrollPositionX={12}
            >
                {renderData.length === 0 ? (
                    <Text
                        componentClasses={styles.noOptions}
                        skin="paragraph-2"
                        tag="div"
                        textColor="secondary-400"
                    >
                        {noOptionsText || texts.no_options_text}
                    </Text>
                ) : (
                    <div style={{paddingRight: 1.5, paddingLeft: 1.5}}>
                        {/* these extra paddings were added to intentionally to fix border scaling issues */}
                        {renderData.map((item, index) => {
                            return (
                                <DropdownItem
                                    key={
                                        (item?.data as {id?: string})?.id ||
                                        `${item.label}-${item.value}`
                                    }
                                    onSelect={() => onSelect(item, index)}
                                    isSelected={item.value === selectedValue}
                                    isFocused={index === focusedIndex}
                                    contentRenderer={itemRenderer}
                                    data={item}
                                    onDoubleClick={onDoubleClick}
                                    skin="primary"
                                />
                            );
                        })}
                    </div>
                )}
            </Scrollbar>
        </div>
    );
});
